/**
 * The block palette, and the habitat classes that map onto it.
 *
 * The data lives in `data/*.json` rather than in this file, so that adding a
 * block does not mean editing code (`Development/CLAUDE.md` rule 7) and so the
 * player guides can be generated from the same file the game loads (D11).
 *
 * ⚠️ Changing a mapping changes generated terrain. Bump `WORLDGEN_VERSION`.
 *
 * ## ⚠️ The key is the identity. The number is only a representation. (T80)
 *
 * `key` — `"birki"` — is what a block *is*. `id` is a compact stand-in for it
 * on the wire and in saved chunks, pinned per key in
 * [`data/palette-lock.json`](../data/palette-lock.json), which is **committed
 * and append-only**.
 *
 * **Why it matters:** ids used to come from a block's position in the list in
 * `tools/build-palette.py`, so inserting one renumbered everything after it. A
 * chunk is a `Uint16Array` of ids and only player *deltas* are stored, so a
 * renumber does not corrupt visibly — it quietly turns every birch somebody
 * placed into something else. Minecraft rebuilt its data model in 1.13 for
 * exactly this reason, and it hurt because they left it late.
 *
 * **So: refer to blocks by key** (`BLOCK.birki`, `blockIdForKey`), never by a
 * numeric literal. `BLOCK_AIR` is the one exception, and only because an empty
 * chunk being all zeroes is itself load-bearing.
 */

import blocksData from '../data/blocks.json' with { type: 'json' };
import appearanceData from '../data/block-appearance.json' with { type: 'json' };
import behaviourData from '../data/block-behaviour.json' with { type: 'json' };
import vistgerdirData from '../data/vistgerdir-blocks.json' with { type: 'json' };
import { TUNING } from './tuning.ts';
import { stateIdForKey, stateOverride, statesOf } from './states.ts';

export interface BlockDef {
  readonly id: number;
  readonly key: string;
  /** English name (D7 — English first). */
  readonly name: string;
  /** Icelandic name. These are names, not vocabulary; they stay in both locales. */
  readonly nameIs: string;
  /** Can you stand on it? Water cannot. */
  readonly solid: boolean;
  /**
   * How tall the block is **drawn**, as a fraction of a metre. Absent means 1.
   *
   * ⚠️ **A rendering height, not terrain.** The block still occupies its whole
   * cell in the chunk array, so this changes neither what is generated nor what
   * is stored, and changing it does **not** need a `WORLDGEN_VERSION` bump. What
   * it changes is the box the mesher draws, and — the part that is easy to miss
   * — **which faces of the neighbours stay visible**, because a short block no
   * longer covers the side of the block next to it.
   */
  readonly height?: number;
  /**
   * Does the crosshair look straight through it? Water does; scrub does not.
   *
   * ⚠️ **Three different questions, and until T95 one flag answered all of
   * them** — because water was the only non-solid block in the palette, so
   * `solid: false` happened to mean "not walkable", "not targetable" and "not
   * jittered" at once. `kjarr` broke that: you walk *through* a bush but you
   * must be able to *look at* one and cut it.
   */
  readonly fluid?: boolean;
  /** Debug/minimap colour. The real look comes from textures later. */
  readonly colour?: string;
  readonly note?: string;
}

export const BLOCKS: readonly BlockDef[] = blocksData.blocks;

/** Block id by key — `BLOCK.mosahraun` rather than a magic number. */
export const BLOCK: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(BLOCKS.map((b) => [b.key, b.id])),
);

export const BLOCK_AIR = 0;

/* A Map rather than an array indexed by id. Indexing an array with a number
   that arrived over the network is what `security/detect-object-injection`
   exists to flag, and while it is harmless for a dense readonly array, a Map
   removes the question instead of suppressing it. Twenty entries — if meshing
   ever proves this is a hot path, that is a profiling result, not a guess
   (newSession §7 rule 7). */
const BY_ID: ReadonlyMap<number, BlockDef> = (() => {
  const out = new Map<number, BlockDef>(BLOCKS.map((b) => [b.id, b]));
  /**
   * 🧱 **T108: every state id resolves to the block it is a state OF.**
   *
   * This one loop is why nothing else in the codebase had to change. A lit
   * hearth is a different number from an unlit one, and it is still a hearth to
   * everything that only wants to know how hard it is to break, whether you can
   * stand on it, or what it is called in Icelandic. `isSolid`, `blockHeight`,
   * `isTargetable`, `isFluid` and `blockBehaviour` all read this map and all of
   * them go on being right for free.
   *
   * ⚠️ **A Map lookup rather than `isStateId` then a lookup**, deliberately.
   * The alternative adds a comparison to a path the mesher walks once per block
   * per remesh, to save entries in a map that has thirty-seven of them.
   */
  for (const b of BLOCKS) {
    for (const stateId of statesOf(b.key)) out.set(stateId, b);
  }
  return out;
})();

/**
 * ⚡ **THE SAME ANSWERS, IN FLAT ARRAYS — and the comment above asked for this.**
 *
 * 🔴 **It said: *"Twenty entries — if meshing ever proves this is a hot path,
 * that is a profiling result, not a guess."*** **Here is the profiling result.**
 *
 * 🎯 **haugur, 2026-09-17, on a MacBook Air M4:** *"my framerate drops to 2 to 5
 * fps as the worl blocks are beeing drawn"* — and from his own report,
 * **96 slow frames in thirty seconds.**
 *
 * 📏 **Measured, on real chunks:**
 *
 * | | |
 * |---|---|
 * | a chunk of solid buried rock | **68 ms, and it draws NOT ONE TRIANGLE** |
 * | `hidesFace` calls in it | **196,608**, each asking the palette **10–14** questions |
 * | one `Map.get` answer | `isFluid` **19.9 ns** · `blockHeight` **29.0 ns** · `isCutout` **46.5 ns** |
 * | the same answer from a flat array | 🔴 **1.7 ns** |
 *
 * 🔴 **One chunk of rock is about 2.7 MILLION hash lookups, against a palette of
 * a few dozen entries that never changes.**
 *
 * ## ⚠️ The safety the Map was protecting, kept
 *
 * The comment above is right that **an id arrives over the network**, and that
 * indexing an array with it is what `security/detect-object-injection` exists
 * to flag. ✅ **So every reader bounds-checks first** — one comparison, against
 * a hash — **and then the index is provably inside an array this module owns
 * and nobody else can reach.** 🔴 **The rule is honoured, not suppressed.**
 *
 * ⚠️ **`BY_ID` stays** and is still the truth. These are a cache of it, built
 * from it in one pass, so they cannot disagree with it — **and anything that is
 * not a hot path should go on using it.**
 */
const TABLE_SIZE = (() => {
  let max = 0;
  for (const id of BY_ID.keys()) if (id > max) max = id;
  return max + 1;
})();

const SOLID = new Uint8Array(TABLE_SIZE);
const FLUID = new Uint8Array(TABLE_SIZE);
const KNOWN = new Uint8Array(TABLE_SIZE);
/**
 * ⚠️ **SIXTY-FOUR bit, not thirty-two, and a test caught the difference.**
 *
 * 🔴 A `Float32Array` cannot hold 0.3: it gives back **0.30000001192092896**.
 * **That is a CHANGED ANSWER**, not a rounding detail — the whole promise of
 * these tables is that they agree with the map exactly. 👉 A double is the same
 * speed here and is exact for every number the palette holds.
 */
const HEIGHT = new Float64Array(TABLE_SIZE);

for (const [id, def] of BY_ID) {
  /* eslint-disable security/detect-object-injection --
     🔴 **The id here comes from `BY_ID`'s OWN KEYS, never from the network** —
     which is the case the rule is written about. The arrays were sized from the
     largest of those keys a few lines above, so every index is provably inside
     one this module made and nobody else can reach. ⚠️ **The READERS are a
     different question and they bounds-check**, because their id really does
     arrive over the wire. */
  /* 🚪 A state may override it — an open gate is not solid (D130). */
  SOLID[id] = (stateOverride(id)?.solid ?? def.solid) ? 1 : 0;
  FLUID[id] = def.fluid ? 1 : 0;
  KNOWN[id] = 1;
  HEIGHT[id] = def.height ?? 1;
  /* eslint-enable security/detect-object-injection */
}

/**
 * The block a number is, or `undefined` if nothing has that number.
 *
 * 🔴 **A state id answers the BLOCK, so `blockById(id).id` is NOT `id`.** For a
 * lit hearth this returns the hearth's own def, whose `id` is the *unlit*
 * number — because that def describes the block and there is only one of those.
 * ⚠️ **Never round-trip a number through here**: `blockById(x)?.id` silently
 * throws away the state. Use `baseBlockOf` from `states.ts` when that is what you meant,
 * and keep the original number when it is not.
 */
export function blockById(id: number): BlockDef | undefined {
  return BY_ID.get(id);
}

/* Same reasoning as BY_ID, for keys arriving from a data file rather than from
   code. `BLOCK[someString]` is the object-injection pattern the linter flags,
   and a Map answers it rather than suppressing it. */
const BY_KEY: ReadonlyMap<string, number> = new Map(BLOCKS.map((b) => [b.key, b.id]));

/**
 * A block key to its id, or `undefined` if nothing has that key.
 *
 * For keys that come from **data** — `buildings.json` naming a wall colour, for
 * instance — where a typo should degrade visibly rather than throw.
 */
export function blockIdForKey(key: string): number | undefined {
  return BY_KEY.get(key);
}

/**
 * 🔴 **A block key OR a state key — `moberg` or `eldstaedi[lit=true]`.**
 *
 * ⚠️ **Two name spaces, looked up in a fixed order, and never merged into
 * one map.** A block key must never accidentally resolve to a state, so the
 * plain palette is asked first and the state registry only if it has no answer.
 *
 * 👉 **This exists because the same three lines had been written twice** — once
 * in the client, applying a `block_update` (protocol 15), and again in the
 * server when `block_edit` learned to carry a facing (protocol 17). **Two copies
 * of a name resolution is how they come to disagree about what a name means.**
 */
export function blockOrStateIdForKey(key: string): number | undefined {
  return BY_KEY.get(key) ?? stateIdForKey(key);
}

/**
 * Does this block stop you walking into it?
 *
 * ⚠️ **Collision ONLY** — see {@link isTargetable} for what the crosshair can
 * hit. They are different questions and `kjarr` is the block that proves it.
 */
export function isSolid(id: number): boolean {
  /* ⚡ Bounds-checked, then a flat read — see the tables above. */
  if (id < 0 || id >= TABLE_SIZE) return false;
  // eslint-disable-next-line security/detect-object-injection -- bounds-checked above, into an array this module owns
  return SOLID[id] === 1;
}

/**
 * 🔴 **Can you aim at this block and hit it?**
 *
 * ## Why this is not `isSolid`
 *
 * `solid` means *"you cannot walk through it"*. **Whether you can hit it is a
 * different question**, and they disagree on exactly one block — which is
 * everywhere. 🌿 **`kjarr` is scrub you walk through** (`solid: false`, T95) and
 * you must still be able to look at one and cut it.
 *
 * ## 🔴 This function is a RENAMING, not a fix. The rule already worked.
 *
 * ⚠️ **Three documents — HANDOFF, ADR-0011 and STATUS — said `kjarr` "cannot be
 * broken at all (`solid: false`)". That was FALSE, and had been since
 * 2026-08-07**, when the client's aim raycast stopped using `isSolid` and
 * started asking *"not air and not a fluid"*. Nothing on the client or the
 * server has refused to break scrub since. **Checked in `validateEdit` and in
 * the `mousedown` handler before writing a line of this.**
 *
 * 👉 **Why the wrong note survived a week: the rule was an unnamed lambda.**
 * There was nothing to grep for, so every session re-read the same claim in the
 * documents and none of them could cheaply disprove it. **Giving a rule a name
 * is what makes it checkable** — that is the whole reason this function exists,
 * along with the server needing to ask the same question when it validates how
 * long a block took to break.
 *
 * ⚠️ **Fifth documented constraint on this project that was not in the code.**
 * `Development/CLAUDE.md` rule 10, and `06_Traps.md`.
 *
 * ## The rule
 *
 * You can hit anything that is really there and is not a fluid. **Air is
 * nothing; water is aimed through** (`isFluid`, so you can build a bridge over
 * a pond rather than onto its surface). Everything else is fair game, whether
 * or not it blocks your feet.
 *
 * ⚠️ **`unbreakable` is deliberately NOT consulted here.** Bedrock can be
 * *aimed* at — you should see what you are pointing at — and the refusal to
 * break it belongs to `clicksToBreak`, which returns `null`. Hiding it from the
 * crosshair would make the floor of the world feel like a rendering fault.
 */
export function isTargetable(id: number): boolean {
  if (id === BLOCK_AIR) return false;
  if (isFluid(id)) return false;
  if (id < 0 || id >= TABLE_SIZE) return false;
  // eslint-disable-next-line security/detect-object-injection -- bounds-checked above, into an array this module owns
  return KNOWN[id] === 1;
}

/**
 * How tall this block is drawn, 0…1. Air is 0; everything unstated is 1.
 *
 * ⚠️ **The mesher's face culling must consult this, not just "is it air".** The
 * old rule — hide a face wherever the neighbour is not air — is only correct
 * when every block is a full cube, which was true until `kjarr` (T95). A
 * half-height tuft of scrub does not cover the side of the block beside it,
 * and culling against it punches holes in terrace risers and tree trunks.
 */
/**
 * Does the crosshair pass through this block?
 *
 * Water only, today. Used by the client's aim raycast and by the mesher, which
 * must not jitter water's brightness — a sea where every square metre is a
 * slightly different shade reads as static.
 */
export function isFluid(id: number): boolean {
  if (id < 0 || id >= TABLE_SIZE) return false;
  // eslint-disable-next-line security/detect-object-injection -- bounds-checked above, into an array this module owns
  return FLUID[id] === 1;
}

/**
 * 🧱 **Does the block BELOW reach up into this point?** — D130, 2026-09-29.
 *
 * 🔴 **A block taller than its cell stands in the cell above it too**, and
 * nothing asked the cell above until the stone wall: `grjotgardur` is 1.5 m, so
 * its top half-metre is in the cell over it, which holds air. **Ask only the
 * cell and a sheep walks through the top of the wall.**
 *
 * @param below the block id one cell down, or `null` if it is not loaded
 * @param intoCell how far into its own cell the point is, 0 … 1
 */
export function reachesUpInto(below: number | null, intoCell: number): boolean {
  if (below === null || !isSolid(below)) return false;
  return intoCell < blockHeight(below) - 1;
}

export function blockHeight(id: number): number {
  if (id === BLOCK_AIR) return 0;
  if (id < 0 || id >= TABLE_SIZE) return 1;
  // eslint-disable-next-line security/detect-object-injection -- bounds-checked above, into an array this module owns
  const h = HEIGHT[id] ?? 1;
  /* ⚠️ A number outside the palette is a full block, as it always was — an
     unknown block must not become a hole in the floor. */
  return h === 0 ? 1 : h;
}

/* ── how a block looks ────────────────────────────────────────────────────── */

/**
 * ⚠️ **Why there are two palette files, and why this is the hand-authored one.**
 *
 * `blocks.json` is **generated** by `tools/build-palette.py` from the vistgerðir
 * habitat raster. That is the right place for what the raster knows — which
 * classes exist, and the measured average colour of each — and the wrong place
 * for everything else, because **a habitat raster has no opinion about how a
 * block should look.**
 *
 * It is also actively dangerous to put a human decision there. `steinsteypa`
 * once lived only in the *generated* file, so running `build-palette.py` would
 * have deleted block 25 and **turned every building into air** (T68). The bug
 * was caught, but the shape of it is permanent: anything a person decides has
 * to live where the generator cannot reach it.
 *
 * So: `data/block-appearance.json`, keyed by **block key** and never by id
 * (T80), merged over the generated palette here. The same pattern is where
 * block *behaviour* should go when it arrives — hardness, luminance, opacity —
 * but in its own file rather than this one, since they are separate concerns
 * with separate owners (`Development/Minecraft/01_The_Block_Model.md` §8.1).
 */
export interface BlockAppearance {
  /** The upward face. */
  readonly top: string;
  /** The four vertical faces. */
  readonly side: string;
  /** The downward face — seen from below, and from inside anything hollow. */
  readonly bottom: string;
}

/** One entry as authored. Every field optional; each falls back to the last. */
interface AuthoredAppearance {
  readonly top?: string;
  readonly side?: string;
  readonly bottom?: string;
  readonly note?: string;
}

/* A Map, for the same reason as BY_KEY: indexing a plain object by a string
   that came out of a data file is the object-injection pattern. */
const AUTHORED: ReadonlyMap<string, AuthoredAppearance> = new Map(
  Object.entries(appearanceData.blocks as Record<string, AuthoredAppearance>),
);

/**
 * ⚠️ The eyesore of last resort, and it should never be seen.
 *
 * Matches the mesher's own fallback so a block with no colour anywhere is
 * unmistakably magenta rather than quietly black.
 */
const NO_COLOUR = '#FF00FF';

const APPEARANCE: ReadonlyMap<number, BlockAppearance> = (() => {
  const out = new Map<number, BlockAppearance>();
  for (const b of BLOCKS) {
    const authored = AUTHORED.get(b.key);
    /* Each face falls back to the one above it, so an entry that names only a
       `top` still describes all six faces, and a block with no entry at all
       behaves exactly as it did before this file existed. */
    const top = authored?.top ?? b.colour ?? NO_COLOUR;
    const side = authored?.side ?? top;
    const bottom = authored?.bottom ?? side;
    const look = { top, side, bottom };
    out.set(b.id, look);
    /**
     * 🧱 **T108, and the same loop as `BY_ID` for the same reason.**
     *
     * ⚠️ **Resolving inside the getter instead was tried and it broke a
     * contract.** `blockAppearance(9999)` has always answered `undefined`, and
     * a getter that called `baseBlockOf` first turned every unknown number into
     * *air* — so a garbage id started coming back as a real appearance. An
     * existing test caught it. Folding the ids in here keeps "a number nobody
     * handed out is unknown" true, because a map only knows what was put in it.
     *
     * ⬜ A LIT hearth will want its own glowing tile and does not have one yet —
     * that is the fire's art slice, and it belongs in `block-appearance.json`
     * keyed by state, not as a branch here.
     */
    for (const stateId of statesOf(b.key)) out.set(stateId, look);
  }
  return out;
})();

/**
 * The colour of each face of a block, or `undefined` if no such block exists.
 *
 * Always returns all three faces. Callers never have to know whether anybody
 * authored an appearance for this block.
 */
export function blockAppearance(id: number): BlockAppearance | undefined {
  return APPEARANCE.get(id);
}

/* ── how a block behaves ──────────────────────────────────────────────────── */

/**
 * ⚠️ **T137 — the third file of this shape, and the question it closes.**
 *
 * `blocks.json` is generated from the habitat raster and **carries no behaviour
 * at all**: the fields are `id, key, name, nameIs, solid`. **T29 recorded that
 * consequence on 2026-08-06 and nobody owned it** — *"any block behaviour has to
 * be authored somewhere `build-palette.py` will not overwrite. Unowned design
 * question, not yet a task."* It stayed unowned until it was blocking three
 * things at once: rock hardness, the campfire's light, and mining speed by tool.
 *
 * The answer is the same one `block-appearance.json` and `items.json` already
 * use: **a hand-authored file keyed by block key**, merged here, that the
 * generator cannot reach.
 *
 * ## 🔴 Hardness is CLICKS, not seconds
 *
 * 🎯 **haugur already thinks in clicks** — *"10 clicks bare-handed, 7 with wood,
 * 2–3 with iron"* — so a block's difficulty is how many hits it takes with bare
 * hands, and a tool divides that. Minecraft uses seconds and a formula; you
 * cannot read one of its numbers and know what it means without doing
 * arithmetic. **The tool multipliers live in `tuning.json`, so the whole table
 * moves together.**
 *
 * ## ⚠️ One gate, deliberately, and it argues with D45
 *
 * 🎯 **haugur, 2026-08-13:** *"i kinda like the minecraft version, if you use
 * wrong tool, block breaks but you cant collect it. its a fun mechanic."* ✅ He
 * is right that it is a good mechanic, and it is in — **but it is one gate, on
 * `grágrýti`, and not a ladder.** **D45** says progression should reward *time*
 * rather than gate on *materials*, and Iceland supplies at most two tool rungs
 * anyway. **D75** records the trade deliberately rather than letting it drift.
 */
export interface BlockBehaviour {
  /**
   * How many hits with bare hands. A tool divides it — {@link clicksToBreak}.
   *
   * ⚠️ **Not seconds, and not Minecraft's hardness number.**
   *
   * ⚠️ **Optional only because `unbreakable` blocks have no click count**, and
   * an entry must carry exactly one of the two. That is enforced by a test
   * rather than by the type: expressing it as a union would need a cast through
   * `unknown` to get the JSON in, which trades a readable type for an
   * unreadable import. **The test says the same thing and says it in English.**
   */
  readonly clicks?: number;
  /**
   * The tool needed to **keep** what you break. Absent means it always drops.
   *
   * 🔴 **The block still breaks without it — you simply get nothing.**
   */
  readonly needsTool?: ToolTier;
  /**
   * 🪓⛏️🥄 **Which tool makes this faster.** Absent means none does.
   *
   * 🔴 **Absent is an answer.** The six without one break in a single click
   * already or never break at all — see {@link blockTool}.
   */
  readonly tool?: ToolKind;
  /** Never breaks, at all. `bedrock` only. */
  readonly unbreakable?: boolean;
  /**
   * 🪵 **Can you pick this up with an empty hand, instead of breaking it?**
   *
   * 🎯 **haugur, 2026-09-02:** *"you aim at it and use empty hand to pick it up,
   * no breaking."*
   *
   * 🔴 **A flag rather than a list, because the list is what failed.** The
   * client decided what a click meant from a hand-written set of "operated"
   * blocks plus the one berry block named in `tuning.json`; driftwood was in
   * neither, so **the click did nothing at all** and haugur found it in play
   * within minutes. `interact.ts` had predicted exactly that.
   */
  readonly pickedByHand?: boolean;
  /**
   * 📦 **Do you OPERATE this block rather than build against it?**
   *
   * A hearth you feed, a chest you open. 🔴 **A flag rather than a list, and the
   * list is what failed.** `interact.ts` decided this from a hand-written set
   * and warned in its own comment that *"the day a second operated block exists,
   * this is what gets forgotten, exactly as the hearth was."*
   *
   * ⚠️ **It had already been forgotten once by then** — driftwood, the same
   * afternoon: the server knew how to pick a heap up and **the client never
   * asked**. The chest is the third such block, so the list became this.
   *
   * ⚠️ **The cost, which was already accepted:** you cannot place a block by
   * aiming at one of these. Aim at the ground beside it.
   */
  readonly operated?: boolean;
  /**
   * Light given off, 0–15. Absent means none.
   *
   * ⚠️ **Nothing emits light today and that is correct, not unfinished** — no
   * block in our palette would emit any in Landnám. The field exists so that
   * **T128** is renderer work rather than a data-model change.
   */
  readonly light?: number;
  /**
   * How much of the tile is hole, 0…1. Absent means a solid cube.
   *
   * 🍃 **T144, and `birkilauf` is the only one.** 🎯 **haugur, looking at dev:**
   * *"i think next design we do and change is leaves, still blocks and funny
   * looking."* 🔴 **The cause was not the picture.** An opaque leaf hides every
   * face it shares with another leaf, so a birch crown was a **hollow green box**
   * — and no new texture could have fixed that, because the faces that would
   * have carried it were never drawn.
   *
   * ⚠️ **One number, two jobs, deliberately.** {@link renderTile} makes this
   * share of the tile transparent and the mesher stops culling against it. They
   * are the same fact — *this block is see-through by this much* — and two
   * fields would be two things to keep in step.
   *
   * ⚠️ **Cutout is not the same as non-solid.** You can still stand on a crown,
   * still aim at one, and still cut it. Only the drawing changed.
   */
  readonly cutout?: number;
  /**
   * 🌸 **Drawn as a CROSS — two planes at 45° — rather than as a box.**
   *
   * See {@link blockIsCross}. ⚠️ **A cross block must also set {@link cutout}**,
   * or the planes are opaque and it reads as folded card rather than foliage.
   */
  readonly cross?: boolean;
  /**
   * 🌊 **The cell is STILL WATER even though something grows in it.**
   *
   * See {@link blockIsWaterlogged}. **T242, D120** — söl and beltisþari.
   */
  readonly waterlogged?: boolean;
  readonly note?: string;
}

/**
 * The tool rungs, and there are **four** since 2026-09-01 (**D103**).
 *
 * 🎯 **haugur:** *"Lets have stone tools, we have grágrýti, lava, móberg,
 * apalhraun, all stone material."*
 *
 * ⚠️ **The stone rung was added against the research, knowingly.** This comment
 * used to say *"no stone rung, and that is a real Icelandic constraint"*, and
 * **the constraint is still real**: there is no flint anywhere in Iceland and
 * whetstones were 98–99 % imported, because there is no stone on the island good
 * enough to make one from — so a basalt **blade** could never have held an edge.
 * The settlers were an iron-using people who used stone for hammers, net
 * sinkers, whorls and loom weights. ✅ **He was shown that finding and the
 * honest alternative — a stone hammer that beats rock and nothing else — and
 * chose the full set anyway.** That is his to choose; it is recorded in
 * `tuning.json` so nobody later mistakes it for an oversight.
 */
export type ToolTier = 'hand' | 'wood' | 'stone' | 'iron';

const TOOL_ORDER: readonly ToolTier[] = ['hand', 'wood', 'stone', 'iron'];

/**
 * 🪓⛏️🥄 **What a tool is FOR.** Three jobs, and a tool is only worth anything
 * on the one it matches.
 *
 * 🎯 **haugur, 2026-09-01, asked whether a tool should have to match the
 * block:** *"Yes, match it."*
 *
 * ⚠️ **Until this existed, a tool sped up everything equally** — a wooden shovel
 * chopped a birch as fast as an axe, so there was no reason to own more than
 * one. **Matching is what makes three tools three decisions.**
 */
export type ToolKind = 'axe' | 'pick' | 'shovel';

/**
 * 🖐️ **What you are holding**, or `null` for bare hands.
 *
 * ⚠️ **Deliberately one object rather than two arguments.** A tier and a kind
 * are never meaningfully apart — an *iron* on its own cannot break anything —
 * and two positional arguments is how a call site ends up passing the kind where
 * the tier goes with nothing to catch it.
 */
export interface HeldTool {
  readonly tier: ToolTier;
  readonly kind: ToolKind;
}

/** What is in your hand for the purposes of breaking. `null` is bare hands. */
export type Held = HeldTool | null;

/**
 * 🪓⛏️🥄 Which tool this block answers to, or `undefined` if none does.
 *
 * 🔴 **`undefined` is an answer, not a gap.** The six blocks without one —
 * `kjarr`, `birkilauf`, `kyndill`, `vatn`, `kvika` and `bedrock` — either break
 * in a single click already or never break at all, and {@link clicksToBreak} has
 * a floor of 1, so **no tool could make them faster even if one were listed.**
 * Naming a tool there would be a lie that reads as a feature.
 */
export function blockTool(id: number): ToolKind | undefined {
  return blockBehaviour(id)?.tool;
}

/**
 * How much this held tool divides the block's clicks by.
 *
 * 🔴 **The wrong tool is worth exactly as much as bare hands — 1.** Not a
 * penalty, not a fraction: a shovel on rock is neither better nor worse than
 * your hands, which is Minecraft's rule and is the one a player can guess.
 */
function speedAgainst(id: number, held: Held): number {
  if (held === null) return 1;
  if (held.kind !== blockTool(id)) return 1;
  return TOOL_SPEED.get(held.tier) ?? 1;
}

/* A Map, for the same object-injection reasoning as everything else here. */
const BEHAVIOUR: ReadonlyMap<string, BlockBehaviour> = new Map(
  Object.entries(behaviourData.blocks as Record<string, BlockBehaviour>),
);

/**
 * 🔴 **How much each tool tier divides a block's clicks by.**
 *
 * ⚠️ **`testOverride` wins where it is present, and it is a TESTING AID.**
 * 🎯 haugur, 2026-09-02, wanting to test the shore without mining for an hour:
 * *"make stone tools x1 hit to break everything and wood tools x2 hits, make a
 * note for use to change this back later."* ✅ **Deleting that one key in
 * `tuning.json` puts the game back** — the D103 numbers underneath it were
 * never edited, so there is nothing else to undo and the Project Brief never
 * stopped being true.
 */
const T = TUNING.tools;
const TOOL_SPEED: ReadonlyMap<ToolTier, number> = new Map([
  ['hand', T.speedBareHanded],
  ['wood', T.testOverride?.speedWoodTool ?? T.speedWoodTool],
  ['stone', T.testOverride?.speedStoneTool ?? T.speedStoneTool],
  ['iron', T.testOverride?.speedIronTool ?? T.speedIronTool],
]);

/**
 * ⚙️ **How much this tier ACTUALLY divides a block's clicks by, right now.**
 *
 * 🔴 **"Right now" is the whole point of exporting it.** `tuning.json` may carry
 * a `testOverride`, and anything that wants to reason about breaking — a test,
 * a tool, a readout — has to ask the same table `clicksToBreak` uses, or it
 * quietly disagrees with the game. ⚠️ **A second copy of this lookup is exactly
 * the "two lists in step" fault this project keeps paying for.**
 */
export function toolSpeed(tier: ToolTier): number {
  return TOOL_SPEED.get(tier) ?? 1;
}

export function blockBehaviour(id: number): BlockBehaviour | undefined {
  const key = BY_ID.get(id)?.key;
  return key === undefined ? undefined : BEHAVIOUR.get(key);
}

/**
 * How many hits to break this block with that tool, or `null` if it never
 * breaks.
 *
 * Rounds to the nearest whole click and **never returns less than 1**, so no
 * tool ever makes something free.
 */
export function clicksToBreak(id: number, held: Held = null): number | null {
  const behaviour = blockBehaviour(id);
  if (behaviour === undefined || behaviour.unbreakable === true) return null;
  if (behaviour.clicks === undefined) return null;
  return Math.max(1, Math.round(behaviour.clicks / speedAgainst(id, held)));
}

/**
 * ⏱️ **How long you must HOLD the button to break this**, in real seconds.
 *
 * 🎯 **haugur, 2026-08-17, choosing hold over repeated clicking:** *"aggree."*
 * **His numbers did not change meaning.** A `click` in `block-behaviour.json` is
 * still the unit of difficulty — his own tree is *"10 clicks bare-handed, 7 with
 * wood, 2–3 with iron"* — it is now how *long* rather than how many presses,
 * because thirty separate clicks on a grágrýti block is a sore hand.
 *
 * `null` for anything that cannot be broken at all, exactly as
 * {@link clicksToBreak} returns it — bedrock, and air.
 */
export function secondsToBreak(id: number, held: Held = null): number | null {
  const clicks = clicksToBreak(id, held);
  if (clicks === null) return null;
  return clicks * TUNING.breaking.perClickRealSeconds;
}

/**
 * Do you get to keep it?
 *
 * 🔴 **This is the gate, and it is the only one.** A block with no `needsTool`
 * always drops. `grágrýti` needs iron: hit it with your hands and it breaks and
 * **you get nothing**, which is the mechanic haugur asked for.
 *
 * 🔴 **The gate checks the KIND as well as the tier, since D102.** An iron
 * *shovel* does not collect grágrýti. ⚠️ **That is not an extra rule bolted on,
 * it is the same rule seen from the other side**: a shovel on rock is worth
 * bare hands, so letting it keep the drop would have been the two halves of one
 * mechanic disagreeing — slow like a hand, rewarded like a pick.
 */
export function dropsWhenBrokenBy(id: number, held: Held = null): boolean {
  const behaviour = blockBehaviour(id);
  if (behaviour === undefined || behaviour.unbreakable === true) return false;
  if (behaviour.needsTool === undefined) return true;
  if (held === null || held.kind !== behaviour.tool) return false;
  return TOOL_ORDER.indexOf(held.tier) >= TOOL_ORDER.indexOf(behaviour.needsTool);
}

/** Light given off, 0–15. Nothing emits any today — see {@link BlockBehaviour}. */
/** Can this block be picked up by hand rather than broken? */
/** Is this a block you operate — a hearth, a chest — rather than build against? */
export function isOperated(id: number): boolean {
  return blockBehaviour(id)?.operated === true;
}

export function isPickedByHand(id: number): boolean {
  return blockBehaviour(id)?.pickedByHand === true;
}

export function blockLight(id: number): number {
  return blockBehaviour(id)?.light ?? 0;
}

/**
 * How much of this block's tile is hole, 0…1. 0 for a solid cube.
 *
 * 🍃 **`birkilauf` only** — see {@link BlockBehaviour.cutout}.
 */
/**
 * ⚡ **How see-through each block is, flat** — the same answer as
 * {@link blockCutout} and the same tables as `SOLID` and `FLUID`.
 *
 * 🔴 **This one was the worst of the lot.** `isCutout` → `blockCutout` →
 * `blockBehaviour` → **a numeric map lookup for the key, then a STRING map
 * lookup for the behaviour.** 📏 Measured at **46.5 ns** against `isFluid`'s
 * 19.9 — **and the mesher asks it twice for every face it considers.**
 *
 * ⚠️ **Built lazily on first use, not at module load**, because `BEHAVIOUR` is
 * defined below this point and a top-level fill would read it empty. 🔴 **A
 * table built too early is the kind of fault that makes one block type
 * invisible and nothing else**, which nobody would find for weeks.
 */
let CUTOUT: Float64Array | null = null;

export function blockCutout(id: number): number {
  CUTOUT ??= (() => {
    const out = new Float64Array(TABLE_SIZE);
    /* eslint-disable security/detect-object-injection --
       The id comes from `BY_ID`'s own keys, never from the network. */
    for (const key of BY_ID.keys()) out[key] = blockBehaviour(key)?.cutout ?? 0;
    /* eslint-enable security/detect-object-injection */
    return out;
  })();
  if (id < 0 || id >= TABLE_SIZE) return 0;
  // eslint-disable-next-line security/detect-object-injection -- bounds-checked above, into an array this module owns
  return CUTOUT[id] ?? 0;
}

/**
 * 🌸 **Is this block drawn as a CROSS — two planes at 45°, not a box?**
 *
 * 🎯 **haugur, 2026-08-25, out loud while driving:** *"flowers, Minecraft has
 * flowers… I remember we discussed some elements that were like two cross
 * sections"* — and the instruction that mattered more: *"let me know what we
 * find out BEFORE we build any more blocks, because there could be new blocks
 * in the future that should rather not be blocks."*
 *
 * 🔴 **`kjarr` is the first, and it was already wrong.** Birch scrub covers about
 * a third of birch ground and was drawn as a **squashed cube**, which is why it
 * read as green lino rather than as bushes.
 *
 * ⚠️ **A cross is a SHAPE, so it lives here beside `cutout` and `height`** —
 * hand-authored behaviour, not generated palette. **ADR-0010's vocabulary is
 * layer, stairs, thin plate and post and has no cross**; it did not reject one,
 * it never considered one. See `Minecraft/13_Things_That_Are_Not_Cubes.md`.
 *
 * 🔴 **A cross block must also be `cutout`** — otherwise the two planes are
 * opaque and it reads as folded card. **The renderer does not enforce that**,
 * because a shape and a texture are different questions; `blocks.test.ts` does.
 */
export function blockIsCross(id: number): boolean {
  return blockBehaviour(id)?.cross === true;
}

/**
 * 🌊 **Is this cell still part of the sea, even though a plant stands in it?**
 *
 * **T242, D120** — söl and beltisþari grow below the waterline, so the cell they
 * occupy used to be `vatn` and in every sense that matters still is.
 *
 * ## 🔴 Why this had to exist, and it was found by READING rather than by shipping
 *
 * The mesher's `hidesFace` opens with
 * `if (isCutout(block) || isCutout(neighbour)) return false` — **so a sea block
 * beside a see-through plant draws its face.** Every söl and every kelp would
 * have been wrapped in water panes: **a glass box around each plant.**
 *
 * ⚠️ **Water's own rule already says why that is wrong** — *"fluid against fluid
 * still hides, and must. A body of water is meant to be a SKIN, not a stack of
 * glass boxes."* A waterlogged cell is part of that body, so the sea hides
 * against it exactly as it hides against itself.
 *
 * ## 🔴 It is NOT the same as {@link isFluid}, and the difference is the point
 *
 * **The crosshair must STOP on a söl** — you have to aim at one to pick it —
 * where it passes straight through water. So this is a **fourth** question
 * beside the three `build-palette.py` separates:
 *
 * | | |
 * |---|---|
 * | `solid` | can you stand on it? | **no** |
 * | `fluid` | does the crosshair ignore it? | 🔴 **no — you must be able to aim** |
 * | `height` | how tall is it drawn? | söl 0.25, kelp a full cell |
 * | 🆕 `waterlogged` | **is the cell still water?** | **yes** |
 *
 * 👉 **Minecraft calls this waterlogging and arrived at it the same way** — see
 * `Development/Minecraft/21_Underwater_Plants.md`.
 */
export function blockIsWaterlogged(id: number): boolean {
  return blockBehaviour(id)?.waterlogged === true;
}

/**
 * Can you see through this block at all?
 *
 * 🔴 **The mesher's culling must ask this.** A face between two solid cubes is
 * never visible and is never emitted — that is the whole optimisation. **A face
 * beside a cutout block IS visible**, through the holes, so it has to be drawn,
 * and the crown of a birch is nothing but such faces.
 *
 * ⚠️ **This is the third question `solid` used to answer by accident**, after
 * "can you walk on it" and "can you aim at it" (T95). Leaves are solid, aimable
 * and see-through all at once.
 */
export function isCutout(id: number): boolean {
  return blockCutout(id) > 0;
}

/**
 * Which block a vistgerðir habitat class becomes.
 *
 * Falls back to the default rather than throwing: an unrecognised class means
 * a patch of ground looks wrong, which is survivable. Refusing to generate the
 * chunk is not. The generator asserts full coverage at build time, so this
 * fallback should be unreachable in practice — see `tools/build-palette.py`.
 */
const VISTGERDIR_TO_BLOCK: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(
    Object.entries(vistgerdirData.classes).map(([code, entry]) => [code, BLOCK[entry.block] ?? 0]),
  ),
);

const VISTGERDIR_DEFAULT = BLOCK[vistgerdirData.default] ?? 0;

export function blockForHabitat(vistgerdirClass: number): number {
  return VISTGERDIR_TO_BLOCK[String(vistgerdirClass)] ?? VISTGERDIR_DEFAULT;
}

/**
 * 🌊 **What is left in the cell when you pick a block out of it.**
 *
 * 🔴 **THIS EXISTS BECAUSE IT WAS A HARD-CODED `BLOCK_AIR` AND haugur STOOD
 * INSIDE THE HOLE IT MADE — #719, *"looks funny after i picked"*.**
 *
 * The pick path was driftwood's, and **driftwood lies on a dry beach**, so air
 * was the obviously right answer and was never a decision at all. ⚠️ **Söl and
 * kelp stand in water**, so taking one left **a bubble of air in the sea**, with
 * the water drawing walls around it — **#711's fault, made by hand instead of
 * by the generator.**
 *
 * ✅ **The flag already knew the answer.** {@link blockIsWaterlogged} means
 * *the cell is still water* — so a waterlogged plant gives the water back and
 * everything else gives air. **No new list, nothing to keep in step.**
 *
 * 👉 **It is a function rather than a line inside the handler so that it can be
 * TESTED.** The version that shipped was three words in the middle of a four
 * thousand line file, and nothing could see it but a person standing in the sea.
 */
export function blockLeftByPicking(id: number): number {
  return blockIsWaterlogged(id) ? (blockIdForKey('vatn') ?? BLOCK_AIR) : BLOCK_AIR;
}
